import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ClassNames, Modal, ModalService } from '@lsq-frontend/common-ui';
import { BehaviorSubject } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';
import { MfaBackupCodesModalComponent } from './mfa-backup-codes-modal.component';
import { UpdatePhoneNumberModalComponent } from './update-phone-number-modal.component';
import { UpdateMfaEnrollmentModalComponent } from './update-mfa-enrollment';
import { User } from '@lsq-frontend/remote-services/enterprise/common';
import {
    GenericApiServiceFactory,
    GenericBoundedApiService
} from '@lsq-frontend/angular-shared/api-service';
import { ApiRoutes } from '@lsq-frontend/angular-shared/app-constants';
import {
    CheckUserBackupCodesAcceptedResponse,
    GenerateBackupCodesResponse
} from '@lsq-frontend/remote-services/enterprise/compass-services';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';

@Injectable({ providedIn: 'root' })
export class MfaBackupCodesService {
    private _generateBackupCodesService: GenericBoundedApiService;
    private _acceptBackupCodesService: GenericBoundedApiService;
    private _checkBackupCodesAcceptedService: GenericBoundedApiService;
    private _mfaPhoneNumber = this._userService.user$.pipe(map((user) => user?.userPhoneNumber));
    private _mfaPhoneNumberLastFour = this._mfaPhoneNumber.pipe(
        map((phoneNumber) => phoneNumber?.substr(phoneNumber.length - 4))
    );

    private user: User | null;

    constructor(
        private _modalService: ModalService,
        protected _router: Router,
        private _userService: UserService,
        protected _genericApiServiceFactory: GenericApiServiceFactory
    ) {
        SingletonServiceGuard.register('MfaBackupCodesService');
        this._userService.user$.subscribe((val) => (this.user = val));
        this._generateBackupCodesService = this._genericApiServiceFactory.createBounded(
            `/${ApiRoutes.Api}/${ApiRoutes.GenerateBackupCodes}`
        );
        this._acceptBackupCodesService = this._genericApiServiceFactory.createBounded(
            `/${ApiRoutes.Api}/${ApiRoutes.AcceptBackupCodes}`
        );
        this._checkBackupCodesAcceptedService = this._genericApiServiceFactory.createBounded(
            `/${ApiRoutes.Api}/${ApiRoutes.CheckBackupCodes}`
        );
    }

    public async checkUserHasBackupCodes(): Promise<boolean> {
        const hasCodes = await this.userHasAcceptedBackupCodes();

        if (!hasCodes) {
            await this.showNewMfaBackupCodesModal();
        }

        return hasCodes;
    }

    public async userHasAcceptedBackupCodes(): Promise<boolean> {
        return ((await this._checkBackupCodesAcceptedService.executeOnce(
            {}
        )) as CheckUserBackupCodesAcceptedResponse).accepted;
    }

    public async showNewMfaBackupCodesModal(navigateToHome = true): Promise<void> {
        const formGroup = new FormGroup({
            hasCopiedMfaBackupCodes: new FormControl(false, [Validators.required])
        });

        const codes: string[] = ((await this._generateBackupCodesService.executeOnce(
            {}
        )) as GenerateBackupCodesResponse).backupCodes;
        const isSubmittingSubject = new BehaviorSubject<boolean>(false);

        this._modalService.showModal(
            new Modal({
                id: 'mfa-backup-code-modal',
                title: 'Security Backup Codes',
                bodyComponent: MfaBackupCodesModalComponent,
                showFooter: true,
                showOkButton: true,
                showCancelButton: false,
                showCloseButton: false,
                clickOutsideToClose: false,
                okButtonText: 'Save and Continue',
                okButtonSize: ClassNames.Block,
                okButtonIsDisabled$: formGroup.valueChanges.pipe(
                    map(({ hasCopiedMfaBackupCodes }) => !hasCopiedMfaBackupCodes),
                    startWith(true)
                ),
                isSubmitting$: isSubmittingSubject,
                data: {
                    formGroup,
                    codes
                },

                onOkClick: async () => {
                    isSubmittingSubject.next(true);
                    await this._acceptBackupCodesService.executeOnce({});
                    this._modalService.closeModal();
                    if (navigateToHome) {
                        await this._userService.navigateToHome();
                    }
                }
            })
        );
    }

    public async showUpdateMfaModal(): Promise<void> {
        const isSubmittingSubject = new BehaviorSubject<boolean>(false);

        const canEnrollInTotp =
            this.user?.userConfigurations != null &&
            this.user?.userConfigurations['totpEnabled'] === 'true';

        if (canEnrollInTotp) {
            this._modalService.showModal(
                new Modal({
                    id: 'update-mfa-enrollment-modal',
                    title: 'Update MFA Enrollment',
                    bodyComponent: UpdateMfaEnrollmentModalComponent,
                    showFooter: true,
                    showOkButton: true,
                    showCancelButton: true,
                    showCloseButton: true,
                    clickOutsideToClose: false,
                    width: '58rem',
                    okButtonText: 'Update MFA Enrollment',
                    okButtonSize: ClassNames.Block,
                    isSubmitting$: isSubmittingSubject,
                    onOkClick: async () => {
                        isSubmittingSubject.next(true);
                        this._modalService.closeModal();
                        await this._router.navigateByUrl('/settings/profile');
                    },
                    onCancelClick: async () => {
                        this._modalService.closeModal();
                        await this._userService.navigateToHome();
                    },
                    onCloseButtonClick: async () => {
                        this._modalService.closeModal();
                        await this._userService.navigateToHome();
                    }
                })
            );
        } else {
            this._modalService.showModal(
                new Modal({
                    id: 'update-phone-number-modal',
                    title: 'Update Phone Number',
                    bodyComponent: UpdatePhoneNumberModalComponent,
                    showFooter: true,
                    showOkButton: true,
                    showCancelButton: true,
                    showCloseButton: true,
                    clickOutsideToClose: false,
                    width: '58rem',
                    okButtonText: 'Update Phone Number',
                    okButtonSize: ClassNames.Block,
                    isSubmitting$: isSubmittingSubject,
                    data: {
                        phoneNumberLastFour: await this._mfaPhoneNumberLastFour
                            .pipe(first())
                            .toPromise()
                    },
                    onOkClick: async () => {
                        isSubmittingSubject.next(true);
                        this._modalService.closeModal();
                        await this._router.navigateByUrl('/settings/profile');
                    },
                    onCancelClick: async () => {
                        this._modalService.closeModal();
                        await this._userService.navigateToHome();
                    },
                    onCloseButtonClick: async () => {
                        this._modalService.closeModal();
                        await this._userService.navigateToHome();
                    }
                })
            );
        }
    }
}
